<script setup lang="ts">
import { computed, nextTick, onMounted } from 'vue';
import { useRoute, RouterLink } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import ConnectWallet from '../components/ConnectWallet.vue';

const auth = useAuthStore();
const route = useRoute();
const cfg = computed(() => auth.config);

/** At least one sign-in method is usable. */
const hasProvider = computed(() =>
  Boolean(cfg.value?.walletEnabled || cfg.value?.telegramBotUsername || cfg.value?.googleEnabled),
);
/** At least one method is unavailable (shown as a secondary note). */
const hasDisabled = computed(() =>
  Boolean(!cfg.value?.walletEnabled || !cfg.value?.telegramBotUsername || !cfg.value?.googleEnabled),
);

// Server-side redirect endpoint. `next` survives the OAuth round-trip via the
// state store, so the user lands where they were originally headed.
const googleHref = computed(() => {
  const next = typeof route.query.next === 'string' ? route.query.next : '';
  return next ? `/auth/google?next=${encodeURIComponent(next)}` : '/auth/google';
});

/**
 * Telegram Login Widget initialiser. The widget script reads its `data-*`
 * attributes once on load and inserts an <iframe> next to itself, so the
 * placeholder div MUST be in the DOM before we append the script.
 */
function mountTelegramWidget(): void {
  const username = cfg.value?.telegramBotUsername;
  const container = document.getElementById('telegram-widget');
  if (!username || !container || container.querySelector('script')) return;
  const callback = `${window.location.origin}/auth/telegram`;
  const s = document.createElement('script');
  s.async = true;
  s.src = 'https://telegram.org/js/telegram-widget.js?22';
  s.setAttribute('data-telegram-login', username);
  s.setAttribute('data-size', 'large');
  s.setAttribute('data-radius', '6');
  s.setAttribute('data-auth-url', callback);
  s.setAttribute('data-request-access', 'write');
  container.appendChild(s);
}

// We mount imperatively from onMounted instead of via watch:
//  - router.beforeEach calls auth.bootstrap() before LoginView is created, so
//    by setup-time `telegramBotUsername` is already set and a watch+immediate
//    fires synchronously before the DOM exists — the script never lands.
//  - From onMounted the placeholder div IS in the DOM; we just need one
//    nextTick() to flush any reactive re-render triggered by a late bootstrap.
onMounted(async () => {
  if (!auth.ready) await auth.bootstrap();
  await nextTick();
  mountTelegramWidget();
});
</script>

<template>
  <div class="min-h-screen flex flex-col items-center justify-center px-4 py-12">
    <RouterLink
      to="/"
      class="text-ink mb-6 focus-ring rounded-md font-display font-bold text-xl tracking-tight"
    >Open Gonka</RouterLink>

    <div class="card p-8 sm:p-10 max-w-md w-full">
      <h1 class="font-display text-2xl font-semibold mb-1">Sign in or create your account</h1>
      <p class="text-muted text-sm mb-6">Pick a method below — no email or password needed.</p>
      <div class="pill mb-6">✨ Free trial tokens on first sign-in</div>

      <div v-if="!auth.ready" class="text-muted text-sm">Loading…</div>

      <div v-else class="flex flex-col gap-4">
        <!-- Wallet -->
        <section
          v-if="cfg?.walletEnabled"
          class="rounded-lg border border-hairline p-5 flex flex-col items-center text-center gap-3"
        >
          <div class="w-11 h-11 rounded-full border border-hairline flex items-center justify-center text-mute">
            <svg viewBox="0 0 24 24" class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M3 7h13a4 4 0 0 1 4 4v6a4 4 0 0 1-4 4H3z" />
              <circle cx="16" cy="13" r="1.5" />
            </svg>
          </div>
          <div>
            <div class="text-sm font-medium text-ink">Wallet</div>
            <div class="text-xs text-muted mt-1">Sign in with GonkaKeeper — your private keys never leave the extension.</div>
          </div>
          <div class="w-full pt-1">
            <ConnectWallet
              :chain-id="cfg.chainId"
              :bech32-prefix="cfg.bech32Prefix"
            />
          </div>
        </section>

        <!-- Telegram -->
        <section
          v-if="cfg?.telegramBotUsername"
          class="rounded-lg border border-hairline p-5 flex flex-col items-center text-center gap-3"
        >
          <div class="w-11 h-11 rounded-full border border-hairline flex items-center justify-center text-[#2AABEE]">
            <svg viewBox="0 0 24 24" class="w-5 h-5" fill="currentColor" aria-hidden="true">
              <path d="M9.78 16.6 9.5 20.2c.36 0 .51-.15.7-.34l1.68-1.6 3.48 2.55c.64.35 1.1.17 1.27-.59l2.3-10.78c.2-.94-.34-1.3-.96-1.07L4.18 13.46c-.92.36-.91.88-.16 1.11l3.27 1.02 7.59-4.78c.36-.22.68-.1.41.14z" />
            </svg>
          </div>
          <div>
            <div class="text-sm font-medium text-ink">Telegram</div>
            <div class="text-xs text-muted mt-1">Sign in with your Telegram account.</div>
          </div>
          <div id="telegram-widget" class="telegram-slot w-full flex justify-center" />
        </section>

        <!-- Google -->
        <section
          v-if="cfg?.googleEnabled"
          class="rounded-lg border border-hairline p-5 flex flex-col items-center text-center gap-3"
        >
          <div class="w-11 h-11 rounded-full border border-hairline flex items-center justify-center">
            <svg viewBox="0 0 24 24" class="w-5 h-5" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.12-1.43.34-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.77.43 3.45 1.18 4.93l3.66-2.83z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z"/>
            </svg>
          </div>
          <div>
            <div class="text-sm font-medium text-ink">Google</div>
            <div class="text-xs text-muted mt-1">Sign in with your Google account.</div>
          </div>
          <a :href="googleHref" class="btn-secondary w-full inline-flex items-center justify-center">
            <span>Continue with Google</span>
          </a>
        </section>

        <!-- No provider available at all — explicit empty state so the card
             never collapses to a bare bullet list. -->
        <div
          v-if="!hasProvider"
          class="rounded-lg border border-hairline bg-surface-soft/60 p-5 text-center"
        >
          <div class="text-sm font-medium text-ink">Sign-in is temporarily unavailable</div>
          <p class="text-xs text-muted mt-1">
            No sign-in method is configured for this deployment yet. Please check back shortly.
          </p>
        </div>

        <!-- Secondary note: only when at least one method works. -->
        <details v-if="hasProvider && hasDisabled" class="text-xs text-muted">
          <summary class="cursor-pointer select-none hover:text-ink">Other sign-in methods</summary>
          <ul class="mt-2 ml-4 list-disc space-y-1">
            <li v-if="!cfg?.walletEnabled">Wallet sign-in is currently disabled.</li>
            <li v-if="!cfg?.telegramBotUsername">Telegram sign-in is not configured for this deployment.</li>
            <li v-if="!cfg?.googleEnabled">Google sign-in is not configured for this deployment.</li>
          </ul>
        </details>
      </div>

      <p class="text-xs text-muted mt-8 text-center">
        By continuing you agree to the <a href="#" class="link-inline">Terms</a> and
        <a href="#" class="link-inline">Privacy Policy</a>.
      </p>
    </div>

    <RouterLink to="/" class="link-mute text-xs mt-6">← Back to home</RouterLink>
  </div>
</template>
